import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Copy, Share2 } from "lucide-react";
import { API, inr, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

const FIELD = "w-full border border-stone-300 bg-white rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B29] transition-colors";
const INDIAN_STATES = ["Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal"];

export default function Affiliate() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState({ investment_amount: 300000, commission_percent: 10, indicative_commission: 30000 });
  const [showForm, setShowForm] = useState(false);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", mobile: "", whatsapp: "", email: "", password: "", city: "", state: "", pan: "", bank_account: "", ifsc: "", upi: "", channel: "WhatsApp" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { API.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const refLink = done ? `${window.location.origin}/products?ref=${done.affiliate.affiliate_id}` : "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await API.post("/affiliate/register", f);
      setDone(data);
      setUser(data.user);
      toast.success("Welcome to the Suvikka Partner Hub");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="affiliate-success">
        <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mt-6">You're In!</h1>
        <p className="text-stone-500 mt-3">Your Affiliate ID is <strong className="text-[#6A1B29]" data-testid="affiliate-id">{done.affiliate.affiliate_id}</strong>
          {done.affiliate.status === "pending" && " (pending approval)"}</p>
        <div className="mt-6 bg-white border border-stone-200 rounded-xl p-5 flex items-center gap-3">
          <input readOnly value={refLink} className="flex-1 text-sm text-stone-600 outline-none bg-transparent" data-testid="affiliate-ref-link" />
          <button onClick={() => { navigator.clipboard.writeText(refLink); toast.success("Link copied"); }} data-testid="affiliate-copy-link"
            className="p-2 rounded-lg bg-stone-900 text-white"><Copy className="w-4 h-4" /></button>
        </div>
        <button onClick={() => nav("/partner-hub")} data-testid="go-partner-hub"
          className="mt-6 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-8 py-3.5 rounded-full">Go to SUVIKKA PARTNER HUB</button>
      </div>
    );
  }

  return (
    <div>
      <Seo title="Suvikka Product Affiliate | Share Products You Love. Earn." description="Join the Suvikka Product Affiliate program and earn commission when your audience shops your favourite beauty, fashion and jewellery picks." canonicalPath="/affiliate" />
      <section className="bg-[#1C1917] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Product Affiliate</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mt-4 max-w-3xl mx-auto leading-tight">Turn Your Recommendations Into Earnings.</h1>
          <p className="text-stone-300 mt-5 max-w-2xl mx-auto text-base sm:text-lg">Love discovering products? Share your favourite Suvikka products with your audience and earn when they shop through you.</p>
          <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-2xl mx-auto mt-10">
            {[["Choose", "Pick products you love"], [`${settings.commission_percent || 10}%`, "Commission on eligible sales"], ["Share", "One tap to your audience"]].map(([v, l], i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6">
                <p className="font-display text-xl sm:text-3xl font-bold text-white">{v}</p>
                <p className="text-[10px] sm:text-xs text-stone-400 mt-1.5">{l}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-stone-500 mt-6 max-w-2xl mx-auto">*Commission is subject to eligible completed purchases, applicable terms, cancellations/refunds and the Suvikka Affiliate Program policy.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <button onClick={() => setShowForm(true)} data-testid="become-affiliate-button"
              className="inline-flex items-center gap-2 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-7 py-3.5 rounded-full transition-colors">
              Become a Product Affiliate <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/products" data-testid="affiliate-page-outlet-cta"
              className="inline-flex items-center gap-2 border border-stone-500 hover:border-white text-white font-medium px-7 py-3.5 rounded-full transition-colors">
              Explore Products
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 text-center">How Product Affiliate Works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10 max-w-4xl mx-auto">
          {[["Choose a Product", "Browse Suvikka beauty, fashion and jewellery and pick products you genuinely love."], ["Get Your Affiliate Link", "Register and share your unique product link on WhatsApp, Instagram or Facebook."], ["Earn When They Shop", `Earn ${settings.commission_percent || 10}% commission on eligible completed purchases through your link.`]].map(([t, d], i) => (
            <div key={i} className="bg-white border border-stone-200 p-6 text-center" data-testid={`affiliate-step-${i}`}>
              <p className="font-display text-4xl font-semibold text-[#6A1B29]/30">0{i + 1}</p>
              <p className="font-semibold text-stone-900 mt-2">{t}</p>
              <p className="text-sm text-stone-500 mt-2">{d}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-stone-500 mt-8">
          Read the <Link to="/affiliate-terms" className="text-[#6A1B29] font-medium underline" data-testid="affiliate-terms-link">Affiliate Program Terms</Link>
        </p>
      </section>

      {showForm && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-20" id="join-form">
          <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900">Join Suvikka Affiliate</h2>
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-testid="affiliate-register-form">
              <input required placeholder="Full Name *" value={f.name} onChange={set("name")} className={FIELD} data-testid="aff-name" />
              <input required placeholder="Mobile Number *" value={f.mobile} onChange={set("mobile")} className={FIELD} data-testid="aff-mobile" />
              <input placeholder="WhatsApp Number" value={f.whatsapp} onChange={set("whatsapp")} className={FIELD} data-testid="aff-whatsapp" />
              <input required type="email" placeholder="Email *" value={f.email} onChange={set("email")} className={FIELD} data-testid="aff-email" />
              <input placeholder="City" value={f.city} onChange={set("city")} className={FIELD} data-testid="aff-city" />
              <select value={f.state} onChange={set("state")} className={FIELD} data-testid="aff-state">
                <option value="">Select State</option>
                {INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <input placeholder="PAN (for payouts, where applicable)" value={f.pan} onChange={set("pan")} className={FIELD} data-testid="aff-pan" />
              <input placeholder="Bank Account Number" value={f.bank_account} onChange={set("bank_account")} className={FIELD} data-testid="aff-bank" />
              <input placeholder="IFSC" value={f.ifsc} onChange={set("ifsc")} className={FIELD} data-testid="aff-ifsc" />
              <input placeholder="UPI ID" value={f.upi} onChange={set("upi")} className={FIELD} data-testid="aff-upi" />
              <select value={f.channel} onChange={set("channel")} className={FIELD} data-testid="aff-channel">
                <option>WhatsApp</option><option>Instagram</option><option>Facebook</option><option>YouTube</option><option>Personal Network</option><option>Other</option>
              </select>
              <input required type="password" minLength={6} placeholder="Create Password *" value={f.password} onChange={set("password")} className={FIELD} data-testid="aff-password" />
              <button disabled={busy} data-testid="affiliate-register-submit"
                className="sm:col-span-2 inline-flex justify-center items-center gap-2 bg-[#6A1B29] hover:bg-[#501325] disabled:opacity-60 text-white font-semibold px-6 py-4 rounded-full transition-colors">
                {busy ? "Joining…" : <>JOIN AS AFFILIATE <Share2 className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </section>
      )}
    </div>
  );
}
